import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./PendingReviews.module.sass";
import Icon from "../Icon";
import { getAuthToken } from "../../utils/authHelpers";
import { authFetch } from "../../utils/authFetch";

const PendingReviews = ({ className, userType }) => {
    const [loading, setLoading] = useState(true);
    const [pendingReviews, setPendingReviews] = useState([]);
    const [processing, setProcessing] = useState({});
    const [detectedUserType, setDetectedUserType] = useState(null);

    useEffect(() => {
        fetchPendingReviews();
    }, []);

    const fetchPendingReviews = async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            
            if (!token) {
                setLoading(false);
                return;
            }

            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            
            // Determine user type (from prop, localStorage, or try both)
            let currentUserType = userType || localStorage.getItem('user_type');
            
            // Try fetching as host first (backward compatibility)
            let response = await authFetch(`${apiBase}/api/reviews/pending-for-host`, {
                method: 'GET'
            }, { requireAuth: false });

            let isCreator = false;
            
            // If host endpoint fails with 403, try creator endpoint
            if (!response.ok && response.status === 403) {
                response = await authFetch(`${apiBase}/api/reviews/pending-for-creator`, {
                    method: 'GET'
                }, { requireAuth: false });
                isCreator = true;
            } else if (currentUserType === 'creator') {
                // If explicitly set as creator, use creator endpoint
                response = await authFetch(`${apiBase}/api/reviews/pending-for-creator`, {
                    method: 'GET'
                }, { requireAuth: false });
                isCreator = true;
            }

            if (response.ok) {
                const data = await response.json();
                if (data.ok && data.reviews) {
                    setDetectedUserType(isCreator ? 'creator' : 'host');
                    setPendingReviews(data.reviews.map(r => ({
                        ...r,
                        author: isCreator ? r.host_name : r.creator_name,
                        content: r.review_text,
                        time: new Date(r.created_at).toLocaleDateString(),
                        avatar: isCreator ? r.host_avatar_url : r.creator_avatar_url
                    })));
                }
            }
        } catch (error) {
            // Don't log auth errors since authFetch handles logout
            if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
                console.error('Error fetching pending reviews:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptReject = async (reviewId, accept) => {
        setProcessing(prev => ({ ...prev, [reviewId]: true }));
        try {
            const token = getAuthToken();
            
            if (!token) {
                alert('Please log in');
                return;
            }

            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            
            // Use the appropriate endpoint based on detected user type
            const endpoint = detectedUserType === 'creator' 
                ? `${apiBase}/api/reviews/accept-creator-review`
                : `${apiBase}/api/reviews/accept-review`;
                
            const response = await authFetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    review_id: reviewId,
                    accept: accept
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.ok) {
                    alert(accept ? 'Review accepted successfully!' : 'Review rejected');
                    fetchPendingReviews();
                } else {
                    alert(data.error || 'Failed to update review');
                }
            } else {
                const errorText = await response.text();
                let errorMessage = 'Failed to update review';
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (e) {
                    errorMessage = errorText || errorMessage;
                }
                alert(errorMessage);
            }
        } catch (error) {
            // Don't log auth errors since authFetch handles logout
            if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
                console.error('Error accepting/rejecting review:', error);
                alert('Failed to update review');
            }
        } finally {
            setProcessing(prev => {
                const newState = { ...prev };
                delete newState[reviewId];
                return newState;
            });
        }
    };

    if (loading) {
        return (
            <div className={cn(className, styles.reviews)}>
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                    Loading pending reviews...
                </div>
            </div>
        );
    }

    return (
        <div className={cn(className, styles.reviews)}>
            <div className={styles.head}>
                <div className={styles.title}>
                    {pendingReviews.length > 0 
                        ? `${pendingReviews.length} Pending Review${pendingReviews.length !== 1 ? 's' : ''}` 
                        : 'Pending Reviews'}
                </div>
            </div>
            <div className={styles.group}>
                {pendingReviews.length > 0 ? (
                    pendingReviews.map((x, index) => (
                    <div className={styles.item} key={x.id || index}>
                        <div className={styles.avatar}>
                            {x.avatar ? (
                                <img src={x.avatar} alt="Avatar" />
                            ) : (
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    color: '#666'
                                }}>
                                    {x.author ? x.author[0].toUpperCase() : '?'}
                                </div>
                            )}
                        </div>
                        <div className={styles.details}>
                            <div className={styles.author}>{x.author}</div>
                            {x.review_title && (
                                <div style={{ 
                                    fontWeight: 600, 
                                    marginBottom: '4px', 
                                    color: '#0f084b' 
                                }}>
                                    {x.review_title}
                                </div>
                            )}
                            <div className={styles.content}>{x.content}</div>
                            <div className={styles.foot}>
                                <div className={styles.time}>{x.time}</div>
                                <div style={{ color: '#dc1d08', fontSize: '14px', marginLeft: '12px' }}>
                                    {'★'.repeat(x.rating || 5)}
                                </div>
                            </div>
                            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => handleAcceptReject(x.id, true)}
                                    disabled={processing[x.id]}
                                    style={{
                                        padding: '6px 16px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        cursor: processing[x.id] ? 'not-allowed' : 'pointer',
                                        opacity: processing[x.id] ? 0.6 : 1
                                    }}
                                >
                                    {processing[x.id] ? 'Processing...' : 'Accept'}
                                </button>
                                <button
                                    onClick={() => handleAcceptReject(x.id, false)}
                                    disabled={processing[x.id]}
                                    style={{
                                        padding: '6px 16px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        cursor: processing[x.id] ? 'not-allowed' : 'pointer',
                                        opacity: processing[x.id] ? 0.6 : 1
                                    }}
                                >
                                    {processing[x.id] ? 'Processing...' : 'Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                    ))
                ) : (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        color: '#666',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px'
                    }}>
                        <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                            No pending reviews
                        </div>
                        <div style={{ fontSize: '14px', color: '#999' }}>
                            {detectedUserType === 'creator' 
                                ? 'Reviews from hosts will appear here for your approval'
                                : 'Reviews from creators will appear here for your approval'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PendingReviews;

